import { useEffect, useState } from 'react';
import { formatDayWord, formattedStringToInt, toReadableNumberPrice } from '../../lib/utils';
import Slider from '../slider';
import { Input } from '../input';
import NumberInput from '../number-input';
import { CheckBox } from '../check-box2';
import styles from './styles.module.scss';

export interface FacilitySellerReportConstants {
  saleDays: SaleDaysPrediction[];
}

interface SaleDaysPrediction {
  marketPriceDifference: number; // Отклонение от рыночной цены (проценты)
  saleDays: number; //Срок продажи (дни)
}

type PriceCalculatorsProps = {
  report?: any;
  reportConstants: FacilitySellerReportConstants;
  selectedPrice: {
    stat: IPriceStat;
    value: number;
  } | null;
  setPriceForBackend: any;
  priceWithoutMarketPrise: any;
  setPriceWithoutMarketPrise: any;
};

interface IPriceStat {
  days: number;
  price: number;
  percent: number;
}

export const ruble = '₽';

const PriceCalculator: React.FC<PriceCalculatorsProps> = (props) => {
  const { report, reportConstants, selectedPrice, setPriceForBackend } = props;
  // prepared data for backend
  const [priceDataForBackend, setPriceDataForBackend] = useState<IPriceStat[]>([]);
  // local price
  const [dayInputValue, setDayInputValue] = useState('');
  // local input price
  const [priceInputValue, setPriceInputValue] = useState('');
  const [priseNotInDiapason, setPriseNotInDiapason] = useState(false);
  const [isSetPrice, setIsSetPrice] = useState(false);

  useEffect(() => {
    const stats: IPriceStat[] = reportConstants?.saleDays?.map((value) => ({
      days: value.saleDays,
      price: price + (price * value.marketPriceDifference) / 100,
      percent: value.marketPriceDifference,
    }));
    setPriceDataForBackend(stats);
    const zeroStat = stats?.find((stat) => stat.percent === 0);
    if (zeroStat) {
      setPriceForBackend({ stat: zeroStat, value: zeroStat.price });
      setDayInputValue(formatDayWord(zeroStat.days));
      setPriceInputValue(toReadableNumberPrice(zeroStat.price, false));
    }
  }, [reportConstants]);

  useEffect(() => {
    return setPriseNotInDiapason(false);
  }, []);

  const handleReset = () => {
    const zeroStat = priceDataForBackend.find((stat) => stat.percent === 0);
    if (zeroStat) {
      setPriceForBackend({ stat: zeroStat, value: zeroStat.price });
      setDayInputValue(formatDayWord(zeroStat.days));
      setPriceInputValue(toReadableNumberPrice(zeroStat.price, false));
    }
    setPriseNotInDiapason(false);
  };

  const handleChangeSliderValue = (field: 'days' | 'percent') => (value: number) => {
    const stat = priceDataForBackend.find((stat) => stat[field] === value);
    if (stat) {
      setPriceForBackend({ stat, value: stat.price });
      setDayInputValue(formatDayWord(stat.days));
      setPriceInputValue(toReadableNumberPrice(stat.price, false));
    }
  };

  const price = report?.marketPrice ?? 0;
  if (!selectedPrice) {
    return null;
  }

  const handleSetPriceWithoutSlider = (value: string) => {
    const priceWithoutSpace = value?.replace(/\s/g, '');
    setPriceInputValue(() => toReadableNumberPrice(+priceWithoutSpace, false));
    setPriceForBackend((prev: any) => ({
      stat: { ...prev.stat, price: +priceWithoutSpace },
      value: +priceWithoutSpace,
    }));
  };

  return report?.marketPrice ? (
    <>
      <div>
        <CheckBox
          name='showArchive'
          label='Установить цену продажи'
          checked={isSetPrice}
          onChange={() => {
            setIsSetPrice((prev) => !prev);
          }}
        />
      </div>
      {priseNotInDiapason || isSetPrice ? (
        <div className={styles.priceCardWithoutMarketPrice}>
          <span className='text-medium'>Выставите желаемую цену</span>
          <NumberInput
            sign={ruble}
            value={priceInputValue}
            onReset={handleReset}
            onChange={handleSetPriceWithoutSlider}
            onBlur={() => {
              setPriceInputValue((prev: any) => {
                return toReadableNumberPrice(+prev.replace(/\s/g, ''), false);
              });
            }}
            className={styles.priceInput}
            disableReset={selectedPrice.stat.percent === 0}
          />
          <span className='text-medium'>Срок продажи неизвестен</span>
        </div>
      ) : (
        <div className={styles.priceCard}>
          <div>
            <span className='text-medium'>Выставите желаемую цену</span>
            <NumberInput
              sign={ruble}
              value={priceInputValue}
              onReset={handleReset}
              className={styles.priceInput}
              disableReset={selectedPrice.stat.percent === 0}
              disabled
            />
            <Slider
              min={-10}
              max={5}
              value={selectedPrice.stat.percent}
              onChange={handleChangeSliderValue('percent')}
              marks={{
                '-10': '-10%',
                '-5': '-5%',
                '0': '0%',
                '5': '5%',
              }}
            />
          </div>

          <div>
            <span className='text-medium'>Укажите время продажи</span>
            <Input
              value={dayInputValue}
              onReset={handleReset}
              className={styles.priceInput}
              disableReset={selectedPrice.stat.percent === 0}
              onFocus={() => setDayInputValue(formattedStringToInt(dayInputValue) + '')}
              disabled
            />
            <Slider
              min={0}
              max={80}
              value={selectedPrice.stat.days}
              onChange={handleChangeSliderValue('days')}
              marks={{
                '0': ' ',
                '10': '10',
                '20': '20',
                '40': '40',
                '60': '60',
                '70': '70 ',
                '80': '80',
              }}
            />
          </div>
        </div>
      )}
    </>
  ) : (
    <div className={styles.priceCardWithoutMarketPrice}>
      <span className='text-medium'>Выставите желаемую цену</span>

      <NumberInput
        sign={ruble}
        value={priceInputValue}
        onReset={handleReset}
        onChange={handleSetPriceWithoutSlider}
        onBlur={() => {
          setPriceInputValue((prev: any) => {
            return toReadableNumberPrice(+prev.replace(/\s/g, ''), false);
          });
        }}
        className={styles.priceInput}
        disableReset={selectedPrice.stat.percent === 0}
      />
      <span className='text-medium'>Срок продажи неизвестен</span>
    </div>
  );
};

export default PriceCalculator;
